import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { ModalHeader } from "@/components/common/ModalHeader";
import { Input } from "@/shadcn/components/ui/input";
import { Textarea } from "@/shadcn/components/ui/textarea";
import {
  useAuth,
  useI18N,
  usePostVenueReview,
  useModalHeight,
} from "common/hooks";
import { useEffect, useRef, useState } from "react";
import { readFile } from "@/helpers/cropImage";
import Zoom from "react-medium-image-zoom";
import { AddPhotoImageData } from "common/types";
import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import { trackEvent } from "common/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";


type AddReviewModalProps = {
  venueId: string;
  venueName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: any;
  onSuccess?: () => void;
};

export function AddReviewModal({
  venueId,
  venueName,
  isOpen,
  onClose,
  venueDetails,
  onSuccess,
}: AddReviewModalProps) {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [accessibilityExperience, setAccessibilityExperience] = useState<
    "cool" | "weird" | ""
  >("");
  const isDark = useDarkMode();
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errors, setErrors] = useState({
    note: false,
    accessibilityExperience: false,
    images: attachedImages.map(() => ({ note: false, selectedOption: false })),
  });
  const { mutate, data, error, status } = usePostVenueReview(true);
  const topRef = useRef(null);
  const noteRef = useRef(null);
  const imageRefs = useRef([]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      trackEvent("experience_shared", {
        business_id: venueDetails.id.toString(),
        photos_count: attachedImages.length,
        place_name: venueDetails.name,
        rating: accessibilityExperience,
      });
      showToast(i18n.get("reviewSavedSuccessfully"), "success");
      setAttachedImages([]);
      setNote("");
      setAccessibilityExperience("");
      setErrors({
        note: false,
        accessibilityExperience: false,
        images: [],
      });
      onSuccess?.();
      onClose();
    }
  }, [data, isDark]);

  const validateFields = () => {
    const newErrors = {
      note: note.trim().length < 5,
      accessibilityExperience: !accessibilityExperience,
      images: attachedImages.map(() => ({
        note: false,
        selectedOption: false,
      })),
    };

    setErrors(newErrors);

    const isAccessibilityExperienceValid = !newErrors.accessibilityExperience;
    const isNoteValid = !newErrors.note;

    if (!isAccessibilityExperienceValid) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (!isNoteValid) {
      noteRef.current.scrollIntoView({ behavior: "smooth" });
    }

    return isAccessibilityExperienceValid && isNoteValid;
  };

  const submit = async () => {
    if (!validateFields()) {
      return;
    }
    if (venueId && user) {
      if (topRef?.current) {
        topRef.current.scrollIntoView({ behavior: "smooth" });
      }

      mutate({
        images: attachedImages,
        venueId: venueId,
        review: note.trim(),
        accessibilityExperience,
      });
    } else {
      showToast(i18n.get("mustBeLoggedInToSubmitReview"), "error");
    }
  };

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
    setErrors((prev) => ({
      ...prev,
      images: [
        ...prev.images,
        ...imgs.map(() => ({ note: false, selectedOption: false })),
      ],
    }));
  };

  const handleExperiencePress = (value: "cool" | "weird") => {
    setAccessibilityExperience(accessibilityExperience === value ? "" : value);
    setErrors((prev) => ({ ...prev, accessibilityExperience: false }));
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      <section
        ref={topRef}
        style={modalStyle}
        className={cn(modalClassName)}
        role="none"
      >
        {status === "pending" && <Loader />}

        <ModalHeader
          title={i18n.get("shareYourExperienceShort")}
          onClose={() => {
            setAttachedImages([]);
            setNote("");
            setAccessibilityExperience("");
            onClose();
          }}
        />

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto"
        >
          <div className="self-stretch pt-2 flex-col justify-start items-center gap-4 flex">
            <div className="self-stretch h-30 pt-2 flex-col justify-start items-start gap-1 flex">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium px-6">
                {i18n.get("tellUsAboutYourExperienceHereAt")}
              </h1>

              <h2 className="self-stretch text-fg-brand-subtle label-m-medium px-6">
                {venueName}
              </h2>

              <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
            </div>

            <div className="flex flex-col items-start gap-4 self-stretch">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium px-6">
                {i18n.get("howWasThisPlaceAboutYourAccessibilityNeeds")}
              </h1>

              <div className="flex items-start gap-4 w-full px-6">
                {/* Option 1 */}
                <button
                  type="button"
                  data-type="Like a boss"
                  onClick={() => handleExperiencePress("cool")}
                  
                  className={cn(
                    "w-full h-11 min-w-11 min-h-11 px-4 py-2 overflow-hidden rounded-lg ring-1 ring-inset flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#19165C]/40 transition-colors",
                    accessibilityExperience === "cool"
                      ? "bg-bg-brand-tertiary-default ring-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                      : "bg-bg-brand-secondary-default ring-border-token-brand-minimal hover:bg-bg-brand-secondary-hover",
                  )}
                >
                  <img
                    src="/images/Orange.svg"
                    alt=""
                    className="w-6 h-6"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      "text-center flex flex-col justify-center label-m-semibold leading-5",
                    )}
                  >
                    {i18n.get("sweet")}
                  </span>
                </button>

                {/* Option 2 */}
                <button
                  type="button"
                  onClick={() => handleExperiencePress("weird")}
                  className={cn(
                    "w-full h-11 min-w-11 min-h-11 px-4 py-2 overflow-hidden rounded-lg ring-1 ring-inset flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#19165C]/40 transition-colors",
                    accessibilityExperience === "weird"
                    ? "bg-bg-brand-tertiary-default ring-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                    : "bg-bg-brand-secondary-default ring-border-token-brand-minimal hover:bg-bg-brand-secondary-hover",
                  )}
                >
                  <img
                    src="/images/OrangeSlice.svg"
                    alt=""
                    className="w-6 h-6"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      "text-center flex flex-col justify-center label-m-semibold leading-5",
                    )}
                  >
                    {i18n.get("sour")}
                  </span>
                </button>
              </div>

              {errors.accessibilityExperience && (
                <div className="text-red-600 label-s-regular px-6">
                  {i18n.get("pleaseSelectYourAccessibilityExperience") ||
                    "Please select your accessibility experience (Sweet or Sour)"}
                </div>
              )}
            </div>

            <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-4 flex px-6">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium">
                {i18n.get("tellUsMoreAboutYourExperience")}
              </h1>

              <div className=" w-full">
                <Textarea
                  id="review"
                  placeholder={i18n.get("describeYourExperienceInMoreDetail")}
                  className="scrollbar-thin h-28"
                  value={note}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                  error={note.length > 0 && note.trim().length < 5}
                />
                <div
                  id="charCount"
                  className={`label-m-medium mt-3 ${
                    note.length > 0 && note.trim().length < 5
                      ? "text-fg-error-subtle"
                      : "text-fg-neutral-subtle"
                  }`}
                >
                  {note.length > 0 && note.trim().length < 5
                    ? i18n.get("pleaseEnterAtLeast5Characters")
                    : `${note.trim().length}/500 characters`}
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 px-6 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div
                    key={index}
                    className="grid gap-2 focus-blue"
                    tabIndex={0}
                  >
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt="attached image"
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal sticky bottom-0 bg-bg-brand-secondary-default z-1000">
          <div className="flex-col md:flex-row justify-end items-center gap-4 flex w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture" // Wrap the entire div with the label
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer focus-blue"
                aria-label="Add photos"
                tabIndex={0}
                role="button"
              >
                <div className="text-center text-fg-neutral-strong title-s-semibold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.trim().length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit review"
              >
                {i18n.get("share")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}
